// game/game-reducer.js: pure reducer for the M4 networked client.
//
// The server holds the real game state, the reducer never
// recomputes rules, never decides whose turn it is, never validates a
// play. It does exactly two things:
//
//   1. Replace `gameState` with the snapshot in each incoming event.
//      Snapshots are full per-seat views; trusting them wholesale means
//      the client cannot drift from server state.
//   2. Surface ephemeral state the server doesn't keep, the timer
//      deadline + last error toast.
//
// On reconnect the server pushes a fresh GAME_INITIALIZED; the reducer
// clobbers any stale local state and the client picks up from server
// truth. No client-side merging, no event replay.
//
// Phase 3 #10: the reducer also accumulates a human-readable `eventLog`
// (oldest → newest, capped) from the same game events it already reduces,
// for the side operations-log panel. GAME_INITIALIZED captures the
// viewer's lua seat (your_seat_index + 1) so the viewer's own actions
// read "You …" while everyone else keeps their stable seat_occupants
// names (#8a/#11b). The log resets on every GAME_INITIALIZED (including
// reattach) so it never replays stale lines after a reconnect.

import { formatGameEvent, appendEvent, rebuildLogFromSnapshot } from './event-log.js';
import { plainSeatLabelsFromSnapshot } from '../views/seat-labels.js';
import { _internals as cardInternals } from '../canvas/cards.js';

const { SUIT_SYMBOL } = cardInternals;

/** @typedef {{
 *   gameState: any|null,
 *   bidTimerDeadlineMs: number|null,
 *   playTimerDeadlineMs: number|null,
 *   timerDurationMs: number|null,
 *   errorToast: {code: string, message?: string}|null,
 *   gameOver: boolean,
 *   gameEndedReason: string|null,
 *   finalScores: {team_a: number, team_b: number}|null,
 *   readyDeadlineMs: number|null,
 *   passedOut: boolean,
 *   pendingAutoPlay: {card_id: string, occupant_id?: string}|null,
 *   lastEventSeq: number,
 *   eventLog: string[],
 *   mySeatIndex: 0|1|2|3|null,
 *   handScoredEvent: any|null,
 *   recapSkipsWait: boolean,
 *   lastOwnTimeoutAt: number|null,
 *   gameId: string|null,
 *   departedSeats: Record<number, true>,
 * }} GameClientState */

/** @type {GameClientState} */
export const INITIAL_GAME_STATE = Object.freeze({
  gameState: null,
  bidTimerDeadlineMs: null,
  playTimerDeadlineMs: null,
  timerDurationMs: null,
  errorToast: null,
  gameOver: false,
  gameEndedReason: null,
  finalScores: null,
  readyDeadlineMs: null,
  passedOut: false,
  pendingAutoPlay: null,
  lastEventSeq: 0,
  eventLog: [],
  mySeatIndex: null,
  handScoredEvent: null,
  // Recap gate rule: skip the wait for the last sweep and HAND_SCORED
  // only when a reattach snapshot is itself already at the hand end
  // (end_of_hand or score). The server never replays HAND_SCORED for
  // that hand. A reattach into any earlier state waits like a live hand.
  recapSkipsWait: false,
  lastOwnTimeoutAt: null,
  // Lua seats whose human left or was kicked this game. The server's
  // later snapshots say bot_held false for them (they cannot come back),
  // so the client keeps the plate's bot mark itself.
  gameId: null,
  departedSeats: Object.freeze({}),
});

/**
 * Build the formatter ctx for an event from its own snapshot + the
 * viewer's captured seat. seatLabelsFromSnapshot returns a {luaSeat →
 * label} map (real names from seat_occupants, "You" for the viewer);
 * wrap it in the seatLabel(luaSeat) lookup the formatter expects.
 *
 * @param {any} snap
 * @param {0|1|2|3|null} mySeatIndex
 */
function eventLogCtx(snap, mySeatIndex) {
  // Item 6: running text uses the plain name; the "· bot" mark is for
  // the name plate only.
  const labels = plainSeatLabelsFromSnapshot(snap, mySeatIndex);
  // T19: seats 1+3 are team_a, the formatter renders scores as US/THEM
  // from the viewer's side.
  const myLua = mySeatIndex != null ? mySeatIndex + 1 : 1;
  return {
    trumpSuit: snap?.trump_suit ?? null,
    seatLabel: (luaSeat) => labels[luaSeat] || `Seat ${luaSeat}`,
    myTeam: /** @type {'team_a'|'team_b'} */ ((myLua === 1 || myLua === 3) ? 'team_a' : 'team_b'),
  };
}

/**
 * W7: did the viewer's own seat just time out? The timeout payloads name
 * the seat by occupant_id (which the reducer never stores), but the
 * snapshot's current_turn_seat is still the timed-out seat — the auto
 * action has not landed yet.
 *
 * @param {GameClientState} state
 */
function isOwnSeatTimeout(state) {
  const myLua = state.mySeatIndex != null ? state.mySeatIndex + 1 : null;
  return myLua != null && state.gameState?.current_turn_seat === myLua;
}

/**
 * W7: short card text for the auto-played toast ("9♦", "10♣").
 * @param {string|null|undefined} cardId
 */
function toastCardText(cardId) {
  if (!cardId || cardId.length !== 2) return 'a card';
  const rank = cardId[0] === 'T' ? '10' : cardId[0];
  return `${rank}${SUIT_SYMBOL[cardId[1]] || '?'}`;
}

/**
 * V1 (batch B2 fix4): is this event the viewer's own table action (a bid,
 * a play, or the dealer discard the viewer just made)? A TIMEOUT_* toast
 * survives every game event EXCEPT the viewer's next such action (which
 * dismisses it) and CLEAR_TOAST (the view's 4 s timer). In particular the
 * server's matching auto BID_SUBMITTED / CARD_PLAYED — sent back-to-back
 * with the timeout in one loop — must not wipe the toast.
 *
 * @param {any} event
 * @param {GameClientState} state
 */
function isViewerTableAction(event, state) {
  const myLua = state.mySeatIndex != null ? state.mySeatIndex + 1 : null;
  if (myLua == null || !event || typeof event !== 'object') return false;
  if (event.type !== 'BID_SUBMITTED' && event.type !== 'CARD_PLAYED'
    && event.type !== 'DEALER_DISCARDED') return false;
  return event.payload?.actor_seat === myLua;
}

/**
 * V1: next errorToast for an action/system event. A TIMEOUT_* toast is
 * sticky: the matching auto action (auto_from_timeout bid, or the
 * pendingAutoPlay-correlated card) keeps it, other seats' actions and
 * system lines keep it, and only the viewer's next own action clears it.
 * Any other toast keeps the old behavior (cleared by the next event).
 *
 * @param {GameClientState} state
 * @param {any} event
 * @param {boolean} isMatchingAuto
 */
function nextToastAfterEvent(state, event, isMatchingAuto) {
  const toast = state.errorToast;
  if (!toast) return null;
  if (typeof toast.code === 'string' && toast.code.startsWith('TIMEOUT_')) {
    if (isMatchingAuto) return toast;
    return isViewerTableAction(event, state) ? null : toast;
  }
  return null;
}

/**
 * @param {GameClientState} state
 * @param {any} event
 * @returns {GameClientState}
 */
export function gameReducer(state, event) {
  if (!event || typeof event !== 'object') return state;
  const seq = typeof event.server_event_seq === 'number' ? event.server_event_seq : state.lastEventSeq;
  switch (event.type) {
    case 'GAME_INITIALIZED': {
      // Always clobber on initialization (includes reattach push). The
      // eventLog resets too so a reconnect never replays stale lines.
      const seatIdx = typeof event.payload?.your_seat_index === 'number'
        ? /** @type {0|1|2|3} */ (event.payload.your_seat_index)
        : null;
      const snap = event.payload?.game_state_snapshot ?? null;
      // Each hand sends a fresh GAME_INITIALIZED; a leaver stays gone for
      // the rest of the same game, so their seat mark carries over.
      const gameId = event.payload?.game_id ?? null;
      const departedSeats = gameId != null && gameId === state.gameId
        ? state.departedSeats : INITIAL_GAME_STATE.departedSeats;
      // B2-15 + W1: a reconnect during the final recap must not swap
      // the game-over screen for a hand recap. When the reattach
      // snapshot shows game_over, the flag + final score come from the
      // SNAPSHOT (a reload never saw GAME_ENDED, so state.gameOver is
      // false), and the recap deadline is kept, or taken from the
      // snapshot when it carries one.
      if (event.payload?.reattach && snap?.score_summary?.game_over) {
        return {
          ...INITIAL_GAME_STATE,
          gameState: snap,
          // The game-over snapshot is past the hand end: no wait.
          recapSkipsWait: true,
          mySeatIndex: seatIdx,
          gameId,
          departedSeats,
          lastEventSeq: seq,
          // F4: a reload during the final hand rebuilds that hand's log
          // from the snapshot instead of showing an empty log.
          eventLog: rebuildLogFromSnapshot(snap, seatIdx),
          gameOver: true,
          gameEndedReason: state.gameEndedReason ?? null,
          // Scores agree in practice (GAME_ENDED carried the same
          // totals); prefer the kept ones so a reconnect never swaps in
          // a stale snapshot's zeros, fall back to the snapshot.
          finalScores: state.finalScores
            ?? (snap?.scores ? { team_a: snap.scores.team_a, team_b: snap.scores.team_b } : null),
          readyDeadlineMs: snap?.ready_deadline_server_ts
            ?? state.readyDeadlineMs ?? null,
        };
      }
      // W1: a non-final reattach still keeps the recap countdown; a
      // fresh (non-reattach) init resets it. B2-fix8: a reattach during
      // the all-pass wait also keeps the pass-out panel — the server
      // replays no HAND_PASSED_OUT, so passedOut comes from the
      // snapshot's hand_passed_out flag (false/absent otherwise).
      // F4: a reattach (reload included) rebuilds the current hand's
      // bids, trump call and tricks from the snapshot; a fresh init
      // starts a new hand with an empty log. The dealer's discarded
      // card is never in any snapshot, so no discard line is rebuilt.
      return {
        ...INITIAL_GAME_STATE,
        gameState: snap,
        // Recap gate rule (see INITIAL_GAME_STATE.recapSkipsWait).
        recapSkipsWait: !!event.payload?.reattach
          && (snap?.current_state === 'end_of_hand' || snap?.current_state === 'score'),
        mySeatIndex: seatIdx,
        gameId,
        departedSeats,
        lastEventSeq: seq,
        eventLog: event.payload?.reattach ? rebuildLogFromSnapshot(snap, seatIdx) : [],
        readyDeadlineMs: event.payload?.reattach
          ? (snap?.ready_deadline_server_ts ?? state.readyDeadlineMs ?? null)
          : null,
        passedOut: event.payload?.reattach ? snap?.hand_passed_out === true : false,
      };
    }
    case 'CARD_PLAYED': {
      const snap = event.payload?.game_state_snapshot;
      // B2-9: the server fires PLAY_TIMEOUT just before the auto CARD_PLAYED.
      // Correlate the two so the log reads as an auto play, never a choice.
      const pending = state.pendingAutoPlay;
      const auto = !!pending && pending.card_id === event.payload?.card_id
        && (!pending.occupant_id || !event.payload?.actor_occupant_id
          || pending.occupant_id === event.payload.actor_occupant_id);
      const ctx = eventLogCtx(snap ?? state.gameState, state.mySeatIndex);
      const line = formatGameEvent(event, auto ? { ...ctx, autoFromTimeout: true } : ctx);
      return {
        ...state,
        gameState: snap ?? state.gameState,
        eventLog: appendEvent(state.eventLog, line),
        bidTimerDeadlineMs: null,
        playTimerDeadlineMs: null,
        timerDurationMs: null,
        pendingAutoPlay: null,
        // V1: the matching auto card keeps a TIMEOUT_* toast; only the
        // viewer's next own play (or the 4 s CLEAR_TOAST) dismisses it.
        errorToast: nextToastAfterEvent(state, event, auto),
        lastEventSeq: seq,
      };
    }
    case 'BID_SUBMITTED':
    case 'DEALER_DISCARDED':
    case 'TRICK_RESOLVED': {
      const snap = event.payload?.game_state_snapshot;
      const gameState = snap ?? state.gameState;
      const line = formatGameEvent(event, eventLogCtx(gameState, state.mySeatIndex));
      return {
        ...state,
        gameState,
        eventLog: appendEvent(state.eventLog, line),
        // Any successful action clears the previous bid/play timer
        // (the server emits a new TIMER_STARTED for the next turn).
        bidTimerDeadlineMs: null,
        playTimerDeadlineMs: null,
        timerDurationMs: null,
        // V1: the server's matching auto bid (auto_from_timeout) keeps a
        // TIMEOUT_* toast; otherwise only the viewer's next own action
        // clears it, other seats' actions and system lines keep it.
        errorToast: nextToastAfterEvent(state, event, event.payload?.auto_from_timeout === true),
        lastEventSeq: seq,
      };
    }
    case 'SEAT_RECLAIMED':
    case 'BOT_SUBSTITUTED': {
      const snap = event.payload?.game_state_snapshot;
      const actorSeat = event.payload?.actor_seat;
      const held = event.type === 'BOT_SUBSTITUTED';
      const departed = held && actorSeat != null && !!state.departedSeats[actorSeat];
      const myLua = state.mySeatIndex != null ? state.mySeatIndex + 1 : null;
      // Item 5: the viewer's own seat taken after a timeout or disconnect
      // comes back at the end of the hand, as the next snapshot will say.
      const ownReclaimable = held && actorSeat === myLua && !departed;
      const gameState = snap ?? (actorSeat && state.gameState ? {
        ...state.gameState,
        seat_occupants: {
          ...state.gameState.seat_occupants,
          [actorSeat]: seatHoldPatch(state.gameState.seat_occupants?.[actorSeat], held, ownReclaimable),
        },
      } : state.gameState);
      // The broadcast seat events have no snapshot. Use the actor seat the
      // hook found from game data and the current labels for their lines.
      const logEvent = departed ? { ...event, payload: { ...event.payload, reason: 'leave' } } : event;
      const line = formatGameEvent(logEvent, eventLogCtx(
        event.type === 'SEAT_RECLAIMED' ? gameState : state.gameState, state.mySeatIndex));
      return {
        ...state,
        gameState,
        eventLog: appendEvent(state.eventLog, line),
        // A substituted turn holder has no human timer while the bot
        // acts. Other seat changes leave this turn's deadline intact.
        bidTimerDeadlineMs: held && actorSeat === state.gameState?.current_turn_seat ? null : state.bidTimerDeadlineMs,
        playTimerDeadlineMs: held && actorSeat === state.gameState?.current_turn_seat ? null : state.playTimerDeadlineMs,
        timerDurationMs: held && actorSeat === state.gameState?.current_turn_seat ? null : state.timerDurationMs,
        errorToast: nextToastAfterEvent(state, event, false),
        lastEventSeq: seq,
      };
    }
    case 'SEAT_DEPARTED': {
      // Synthetic, from useGameWs: the room says this seat's human left or
      // was kicked. A bot keeps the seat for the rest of the game. If the
      // bot already held it, no BOT_SUBSTITUTED follows, so say it here.
      const seat = event.payload?.actor_seat;
      if (seat == null || state.departedSeats[seat]) return state;
      const occ = state.gameState?.seat_occupants?.[seat] ?? state.gameState?.seat_occupants?.[String(seat)];
      const line = occ?.bot_held ? formatGameEvent(event, eventLogCtx(state.gameState, state.mySeatIndex)) : null;
      return {
        ...state,
        departedSeats: { ...state.departedSeats, [seat]: true },
        eventLog: appendEvent(state.eventLog, line),
      };
    }
    case 'HAND_SCORED': {
      const snap = event.payload?.game_state_snapshot;
      const line = formatGameEvent(event, eventLogCtx(snap ?? state.gameState, state.mySeatIndex));
      return {
        ...state,
        gameState: snap ?? state.gameState,
        handScoredEvent: event.payload,
        eventLog: appendEvent(state.eventLog, line),
        // B2-5: the server's 30 s auto-advance deadline drives the
        // "Next hand in N s" countdown under the Continue button.
        readyDeadlineMs: event.payload?.ready_deadline_server_ts ?? state.readyDeadlineMs,
        bidTimerDeadlineMs: null,
        playTimerDeadlineMs: null,
        timerDurationMs: null,
        // V1: a system line, not the viewer's action — keep a TIMEOUT_* toast.
        errorToast: nextToastAfterEvent(state, event, false),
        lastEventSeq: seq,
      };
    }
    case 'HAND_PASSED_OUT': {
      // B2-7: everyone passed twice with stick the dealer off. No recap,
      // no timer ring; the view shows the pass-out message + countdown.
      const line = formatGameEvent(event, eventLogCtx(state.gameState, state.mySeatIndex));
      return {
        ...state,
        passedOut: true,
        readyDeadlineMs: event.payload?.ready_deadline_server_ts ?? null,
        eventLog: appendEvent(state.eventLog, line),
        bidTimerDeadlineMs: null,
        playTimerDeadlineMs: null,
        timerDurationMs: null,
        // V1: the pass-out line can land right behind the matching auto
        // pass bid — a system line, so it keeps a TIMEOUT_* toast.
        errorToast: nextToastAfterEvent(state, event, false),
        lastEventSeq: seq,
      };
    }
    case 'HAND_READY_ADVANCED': {
      // The wait is over (next hand dealt, or the final recap ended).
      // Item 8a: the hand's HAND_SCORED totals end with it.
      return {
        ...state,
        passedOut: false,
        readyDeadlineMs: null,
        pendingAutoPlay: null,
        handScoredEvent: null,
        lastEventSeq: seq,
      };
    }
    case 'BID_TIMER_STARTED':
      return {
        ...state,
        bidTimerDeadlineMs: event.payload?.deadline_server_ts ?? null,
        playTimerDeadlineMs: null,
        timerDurationMs: event.payload?.duration_ms ?? null,
        lastEventSeq: seq,
      };
    case 'PLAY_TIMER_STARTED':
      return {
        ...state,
        bidTimerDeadlineMs: null,
        playTimerDeadlineMs: event.payload?.deadline_server_ts ?? null,
        timerDurationMs: event.payload?.duration_ms ?? null,
        lastEventSeq: seq,
      };
    case 'BID_TIMEOUT':
      // The server fires a matching BID_SUBMITTED / CARD_PLAYED right
      // after, which will clear the timer. We clear here too so the
      // bar disappears immediately for the auto-acting seat.
      // W7: when the viewer's OWN seat timed out, toast what the server
      // is about to auto-do (mirroring the B2-9 log line) as well as
      // the log. At the strike limit there is no auto bid (auto_action
      // null — the bot takes over), so no toast. V1: a pass toasts
      // "auto-passed", a naming bid toasts "auto-called <suit>".
      {
        const autoAction = event.payload?.auto_action;
        const ownBidTimeout = isOwnSeatTimeout(state) && !!autoAction;
        const trumpSuit = state.gameState?.trump_suit ?? null;
        const upCard = state.gameState?.up_card;
        const upCardSuit = typeof upCard === 'string' && upCard.length >= 2 ? upCard.slice(-1) : null;
        const glyph = SUIT_SYMBOL[event.payload?.suit ?? trumpSuit ?? upCardSuit] || 'trump';
        const alone = autoAction === 'go_alone' ? ' and are going alone' : '';
        return {
          ...state,
          bidTimerDeadlineMs: null,
          playTimerDeadlineMs: null,
          timerDurationMs: null,
          errorToast: ownBidTimeout
            ? {
              code: 'TIMEOUT_AUTO_BID',
              message: autoAction === 'pass'
                ? 'You ran out of time; auto-passed'
                : `You ran out of time; auto-called ${glyph}${alone}`,
            }
            : state.errorToast,
          lastOwnTimeoutAt: ownBidTimeout ? Date.now() : state.lastOwnTimeoutAt,
          lastEventSeq: seq,
        };
      }
    case 'PLAY_TIMEOUT': {
      // B2-9: remember the auto card so the CARD_PLAYED that follows
      // logs as "ran out of time; auto-played …".
      // W7: ditto the toast when it is our own seat.
      const autoCard = event.payload?.auto_card_id;
      const ownPlayTimeout = isOwnSeatTimeout(state);
      return {
        ...state,
        bidTimerDeadlineMs: null,
        playTimerDeadlineMs: null,
        timerDurationMs: null,
        errorToast: ownPlayTimeout
          ? {
            code: 'TIMEOUT_AUTO_PLAY',
            message: typeof autoCard === 'string' && autoCard
              ? `You ran out of time; auto-played the ${toastCardText(autoCard)}`
              : 'You ran out of time; auto-played a card',
          }
          : state.errorToast,
        lastOwnTimeoutAt: ownPlayTimeout ? Date.now() : state.lastOwnTimeoutAt,
        pendingAutoPlay: typeof autoCard === 'string' && autoCard
          ? { card_id: autoCard, occupant_id: event.payload?.actor_occupant_id }
          : state.pendingAutoPlay,
        lastEventSeq: seq,
      };
    }
    case 'GAME_ENDED': {
      const snap = event.payload?.game_state_snapshot;
      return {
        ...state,
        gameState: snap ?? state.gameState,
        gameOver: true,
        // C4 (2026-06-11): the engine-corruption tripwire ends games with
        // reason 'engine_failure'; the view tells the player why instead
        // of a bare "Game over".
        gameEndedReason: event.payload?.reason ?? null,
        // B2-8: the final score + the recap acknowledgement deadline.
        finalScores: event.payload?.final_scores
          ?? (snap?.scores ? { team_a: snap.scores.team_a, team_b: snap.scores.team_b } : null),
        readyDeadlineMs: event.payload?.ready_deadline_server_ts ?? state.readyDeadlineMs,
        bidTimerDeadlineMs: null,
        playTimerDeadlineMs: null,
        timerDurationMs: null,
        lastEventSeq: seq,
      };
    }
    case 'COMMAND_REJECTED':
      // B2-13: NOT_IN_ROOM means the client already left the room view;
      // it must not surface as an error toast anywhere.
      if (event.payload?.error?.code === 'NOT_IN_ROOM') return state;
      // A repeated ready is not an error: the server already counted this
      // seat's ready (HAND_ALREADY_ADVANCING), and the hook treats it as
      // success. No toast.
      if (event.payload?.command_type === 'MARK_HAND_READY'
        && event.payload?.error?.code === 'HAND_ALREADY_ADVANCING') return state;
      if (event.payload?.error?.code === 'NOT_YOUR_TURN'
        && ['BID', 'PLAY_CARD', 'DEALER_DISCARD'].includes(event.payload?.command_type)
        && state.lastOwnTimeoutAt != null && Date.now() - state.lastOwnTimeoutAt <= 2_000) return state;
      return {
        ...state,
        errorToast: event.payload?.error
          ? { code: event.payload.error.code, message: event.payload.error.message }
          : null,
        lastEventSeq: seq,
      };
    // C4 (2026-06-11): real toast dismissal. The view auto-dismisses
    // after a few seconds; previously CLEAR_TOAST was a documented no-op
    // and a rejection toast stuck around until the next game event.
    case 'CLEAR_TOAST':
      return state.errorToast ? { ...state, errorToast: null } : state;
    default:
      return state;
  }
}

/**
 * Seat-hold fields after BOT_SUBSTITUTED (held) or SEAT_RECLAIMED.
 * @param {any} occ
 * @param {boolean} held
 * @param {boolean} ownReclaimable
 */
function seatHoldPatch(occ, held, ownReclaimable) {
  const rest = { ...(occ || {}) };
  delete rest.reclaim_at;
  return ownReclaimable
    ? { ...rest, bot_held: true, reclaim_at: 'next_hand_end' }
    : { ...rest, bot_held: held };
}

/**
 * The snapshot with every departed seat marked `departed: true`, so the
 * plate keeps its bot mark after the server stops calling it bot_held.
 * @param {any} snap
 * @param {Record<number, true>} departedSeats
 */
function withDepartedSeats(snap, departedSeats) {
  const seats = Object.keys(departedSeats || {});
  if (!snap || seats.length === 0) return snap;
  const occs = { ...(snap.seat_occupants || {}) };
  for (const seat of seats) {
    const key = seat in occs ? seat : Number(seat);
    occs[key] = { ...(occs[key] || {}), departed: true };
  }
  return { ...snap, seat_occupants: occs };
}

/**
 * Item 8a: the stored HAND_SCORED payload belongs to one hand. Hand it to
 * the recap only while the table shows that same hand.
 * @param {any} scored
 * @param {any} snap
 */
function scoredEventForHand(scored, snap) {
  if (!scored) return null;
  const scoredHand = scored.game_state_snapshot?.hand_index;
  const shownHand = snap?.hand_index;
  if (typeof scoredHand === 'number' && typeof shownHand === 'number' && scoredHand !== shownHand) return null;
  return scored;
}

/**
 * Derive the convenience selectors the UI cares about from the reducer
 * state + the local player's seat. Pure.
 *
 * @param {GameClientState} state
 * @param {0|1|2|3|null} mySeatIndex
 */
export function selectGameView(state, mySeatIndex) {
  const snap = withDepartedSeats(state.gameState, state.departedSeats);
  const myLuaSeat = mySeatIndex == null ? null : mySeatIndex + 1;
  const handsObj = snap?.hands ?? {};
  const handVal = myLuaSeat != null ? handsObj[String(myLuaSeat)] : null;
  // hands inner is also a Lua-table-as-JSON-object → Object.values.
  const myHand = handVal && typeof handVal === 'object'
    ? Object.values(handVal).filter((c) => typeof c === 'string')
    : [];
  const isMyTurn = myLuaSeat != null && snap?.current_turn_seat === myLuaSeat;
  // M4-12: server-supplied legality for the viewer. Either field is
  // present only when it's the viewer's turn AND we're in the matching
  // phase (bid_r1/bid_r2 → your_legal_bids; trick_play → your_legal_plays).
  // parity.serialize emits Lua tables as JSON objects keyed by string,
  // so we Object.values() to get a real array.
  const legalBids = isMyTurn && snap?.your_legal_bids
    ? Object.values(snap.your_legal_bids).filter((b) => b && typeof b === 'object')
    : [];
  const legalPlays = isMyTurn && snap?.your_legal_plays
    ? Object.values(snap.your_legal_plays).filter((c) => typeof c === 'string')
    : [];
  return {
    gameState: snap,
    myHand,
    isMyTurn,
    currentState: snap?.current_state ?? null,
    bidTimerDeadlineMs: state.bidTimerDeadlineMs,
    playTimerDeadlineMs: state.playTimerDeadlineMs,
    timerDurationMs: state.timerDurationMs,
    gameOver: state.gameOver,
    gameEndedReason: state.gameEndedReason ?? null,
    finalScores: state.finalScores ?? null,
    readyDeadlineMs: state.readyDeadlineMs ?? null,
    passedOut: !!state.passedOut,
    errorToast: state.errorToast,
    legalBids,
    legalPlays,
    // Phase 3 #10: ordered, readable operations log (oldest → newest).
    eventLog: state.eventLog,
    handScoredEvent: scoredEventForHand(state.handScoredEvent, state.gameState),
    recapSkipsWait: state.recapSkipsWait,
  };
}
